import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Search } from "lucide-react";
import { Switch } from "../ui/switch";
import { yoolColors } from "../../styles/tokens";

/* ============================================================================
   Divina · GroupedTable — tabla agrupada y colapsable (rediseño Yool)
   ---------------------------------------------------------------------------
   Reemplaza el "mapa de cards" de Productos y Planes por una tabla densa,
   agrupada y escaneable. Reutiliza:
     · Switch centralizado (toggle activo/inactivo, teal = acción)
     · Tokens yoolColors  ·  íconos lucide-react (stroke)
   Reglas Yool respetadas: teal #007D6E reservado al CTA/toggle, estados con
   verde éxito / slate / violet (nunca teal decorativo), hairline 1px #E0E0E0,
   sin sombras fuertes, sin gradientes, sin emojis.
   ============================================================================ */

const GT = {
  border: yoolColors.border,        // #E0E0E0
  line: "#EEF1F2",
  text: yoolColors.black,           // #1A1A1A
  sub: "#54585A",                   // yoolColors.gray1
  muted: yoolColors.muted,          // #8D8D8D
  groupBg: "#F5F7F8",
  groupBgHover: "#EEF1F2",
  cta: yoolColors.cta,              // #007D6E
};

/* ── Toggle estado (activo/inactivo) — usa el Switch centralizado ─────────── */
export function StateToggle({ on, onChange, disabled }) {
  return (
    <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex" }}>
      <Switch
        size="sm"
        checked={Boolean(on)}
        disabled={disabled}
        onCheckedChange={(v) => onChange(v)}
        aria-label={on ? "Desactivar" : "Activar"}
      />
    </span>
  );
}

/* ── Chip de compañía — punto neutro + nombre ─────────────────────────────── */
export function CompanyChip({ name }) {
  const none = !name;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13,
        color: none ? GT.muted : GT.text, whiteSpace: "nowrap",
        fontStyle: none ? "italic" : "normal",
      }}
    >
      <span
        style={{
          width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
          background: none ? "#FFFFFF" : "#64748B",
          boxShadow: none ? "inset 0 0 0 1px " + GT.border : "none",
        }}
      />
      {name || "Sin compañía"}
    </span>
  );
}

/* ── Pill de estado — verde éxito / slate / outline (nunca teal) ──────────── */
const PILL = {
  activo:     { c: "#0A5C36", bg: "#DCF3E6", t: "Activo" },
  inactivo:   { c: "#54585A", bg: "#ECECEC", t: "Inactivo" },
  archivado:  { c: "#6B7280", bg: "#F3F4F6", t: "Archivado", ring: true },
};
export function StatusPill({ estado, children }) {
  const p = PILL[String(estado || "").toLowerCase()] || PILL.inactivo;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5,
        fontWeight: 600, padding: "3px 10px 3px 8px", borderRadius: 999,
        background: p.bg, color: p.c, whiteSpace: "nowrap", letterSpacing: "-0.005em",
        boxShadow: p.ring ? "inset 0 0 0 1px " + GT.border : "none",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: p.c, flexShrink: 0 }} />
      {children || p.t}
    </span>
  );
}

/* ── Badge de tipo de plan — Principal (verde) / Complementario (violet) ──── */
export function TipoBadge({ complementario }) {
  const s = complementario
    ? { bg: "#F2E4FE", c: "#7E22CE", t: "Complementario" }
    : { bg: "#E6F2F0", c: "#0A5C36", t: "Principal" };
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", fontSize: 11.5, fontWeight: 600,
        padding: "3px 10px", borderRadius: 999, background: s.bg, color: s.c, whiteSpace: "nowrap",
      }}
    >
      {s.t}
    </span>
  );
}

/* ── Indicador de documentos obligatorios (x/total) ───────────────────────── */
export function DocsCell({ docs, total = 3, names = [] }) {
  const complete = docs >= total;
  const c = complete ? "#158B5C" : docs === 0 ? "#C0004A" : "#B7791F";
  const title = names.length
    ? names.map((n, i) => `${i < docs ? "✓" : "○"} ${n}`).join("\n")
    : `${docs} de ${total} archivos`;
  return (
    <span title={title} style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", cursor: "default" }}>
      <span style={{ display: "inline-flex", gap: 3 }}>
        {Array.from({ length: total }).map((_, i) => (
          <span key={i} style={{ width: 6, height: 16, borderRadius: 3, background: i < docs ? c : "#E6E8EA" }} />
        ))}
      </span>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: c, fontVariantNumeric: "tabular-nums" }}>
        {docs}/{total}
      </span>
    </span>
  );
}

/* ── Tabs de estado (Todos / Activos / …) con conteo ──────────────────────── */
export function StatusTabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 2, borderBottom: "1px solid " + GT.border, flexWrap: "wrap" }}>
      {tabs.map((t) => {
        const on = active === t.value;
        return (
          <button
            key={t.value}
            type="button"
            onClick={() => onChange(t.value)}
            style={{
              position: "relative", border: 0, background: "transparent", cursor: "pointer",
              fontFamily: "inherit", fontSize: 13.5, fontWeight: on ? 600 : 500,
              color: on ? GT.cta : GT.sub, padding: "9px 14px 12px", transition: "color .15s ease",
            }}
          >
            {t.label}
            {t.count != null && (
              <span
                style={{
                  marginLeft: 7, fontSize: 11.5, fontWeight: 600,
                  color: on ? GT.cta : GT.muted, background: on ? "#E2F1EE" : "#EEF0F1",
                  borderRadius: 999, padding: "1px 7px",
                }}
              >
                {t.count}
              </span>
            )}
            {on && <span style={{ position: "absolute", left: 8, right: 8, bottom: -1, height: 2, background: GT.cta, borderRadius: 2 }} />}
          </button>
        );
      })}
    </div>
  );
}

/* ── Segmented control "Agrupar/Filtrar" ──────────────────────────────────── */
export function GroupToggle({ value, options, onChange, label }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {label && <span style={{ fontSize: 12, color: GT.muted, whiteSpace: "nowrap" }}>{label}</span>}
      <div style={{ display: "inline-flex", background: "#EEF0F1", borderRadius: 9, padding: 2 }}>
        {options.map((o) => {
          const on = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              style={{
                border: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600,
                padding: "5px 11px", borderRadius: 7, background: on ? "#fff" : "transparent",
                color: on ? GT.text : GT.sub, boxShadow: on ? "0 1px 2px rgba(0,0,0,.08)" : "none",
                transition: "all .15s ease", whiteSpace: "nowrap",
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Acciones de fila — botones-ícono compactos (32px) ────────────────────── */
export function RowActions({ children }) {
  return <div style={{ display: "flex", alignItems: "center", gap: 2, justifyContent: "flex-end" }}>{children}</div>;
}

export function ActionButton({ icon: Icon, title, onClick, to, danger, disabled }) {
  const [h, setH] = useState(false);
  const navigate = useNavigate();
  const handleClick = (e) => {
    e.stopPropagation();
    if (disabled) return;
    if (onClick) onClick(e);
    if (to) navigate(to);
  };
  return (
    <button
      type="button"
      title={title}
      onClick={handleClick}
      disabled={disabled}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        width: 32, height: 32, display: "grid", placeItems: "center", borderRadius: 9,
        cursor: disabled ? "not-allowed" : "pointer",
        border: "1px solid " + (h ? (danger ? "#F4B8C9" : GT.border) : "transparent"),
        background: h ? (danger ? "#FCEDF1" : "#F7F8F9") : "transparent",
        color: danger ? "#C0004A" : h ? GT.text : GT.sub,
        opacity: disabled ? 0.45 : 1, transition: "all .15s ease",
      }}
    >
      <Icon size={16} />
    </button>
  );
}

/* ── Encabezado de grupo (colapsable) ─────────────────────────────────────── */
function GroupHead({ group, open, onToggle, colSpan }) {
  const [h, setH] = useState(false);
  return (
    <tr onClick={onToggle} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ cursor: "pointer", userSelect: "none" }}>
      <td
        colSpan={colSpan}
        style={{
          padding: "0 16px", height: 46, background: h ? GT.groupBgHover : GT.groupBg,
          borderBottom: "1px solid " + GT.border, borderTop: "1px solid " + GT.border,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <ChevronRight
            size={16}
            style={{ color: GT.sub, flexShrink: 0, transform: open ? "rotate(90deg)" : "none", transition: "transform .15s ease" }}
          />
          <span style={{ fontSize: 13.5, fontWeight: 700, color: GT.text, letterSpacing: "-0.01em" }}>{group.title}</span>
          {group.badge}
          <span style={{ fontSize: 12, color: GT.muted, marginLeft: 2 }}>
            {group.count} {group.count === 1 ? group.unit : group.unitPlural}
          </span>
          {group.meta != null && (
            <span style={{ marginLeft: "auto", fontSize: 12, color: GT.sub, display: "flex", alignItems: "center", gap: 16 }}>
              {group.meta}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ── Tabla agrupada ───────────────────────────────────────────────────────── */
export function GroupedTable({ columns, groups, search, q, setQ, groupToggle, empty, totalShown, totalAll }) {
  const [collapsed, setCollapsed] = useState({});
  const toggle = (id) => setCollapsed((s) => ({ ...s, [id]: !s[id] }));
  const isEmpty = groups.every((g) => g.rows.length === 0);

  const thStyle = (c) => ({ width: c.width, minWidth: c.minWidth, textAlign: c.align || "left" });
  const tdStyle = (c) => ({
    width: c.width, minWidth: c.minWidth, textAlign: c.align || "left",
    fontVariantNumeric: c.numeric ? "tabular-nums" : "normal",
    ...(c.cellStyle || {}),
  });

  return (
    <div style={{ background: '#fff', border: `1px solid ${GT.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 18px', gap: 12, flexWrap: 'wrap', borderBottom: `1px solid ${GT.border}`, background: 'var(--yool-page)' }}>
        {search != null && (
          <div style={{ position: "relative", flex: "1 1 240px", minWidth: 200, maxWidth: 360 }}>
            <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: GT.muted, pointerEvents: "none" }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={search}
              style={{
                width: "100%", height: 38, padding: "0 12px 0 36px", borderRadius: 10,
                border: "1.5px solid " + GT.border, fontSize: 13, color: GT.text, outline: "none",
                fontFamily: "inherit", boxSizing: "border-box", background: "#fff",
              }}
              onFocus={(e) => (e.target.style.borderColor = GT.cta)}
              onBlur={(e) => (e.target.style.borderColor = GT.border)}
            />
          </div>
        )}
        {groupToggle && <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>{groupToggle}</div>}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", minWidth: 880, borderCollapse: "collapse", tableLayout: "fixed" }}>
          <thead style={{ background: 'var(--yool-page)', borderBottom: `1px solid ${GT.border}` }}>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={{ ...thStyle(c), padding: '10px 16px', fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--yool-muted)', whiteSpace: 'nowrap', textAlign: thStyle(c).textAlign || 'left' }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!isEmpty &&
              groups.map((g) =>
                g.rows.length > 0 ? (
                  <React.Fragment key={g.id}>
                    <GroupHead group={g} open={!collapsed[g.id]} onToggle={() => toggle(g.id)} colSpan={columns.length} />
                    {!collapsed[g.id] &&
                      g.rows.map((r) => (
                        <tr
                          key={r.id}
                          onClick={r._onClick}
                          style={{ cursor: r._onClick ? "pointer" : "default", opacity: r._dim ? 0.55 : 1, transition: 'background .1s' }}
                          onMouseEnter={e => r._onClick && (e.currentTarget.querySelectorAll('td').forEach(td => (td.style.background = 'rgba(0,125,110,.05)')))}
                          onMouseLeave={e => (e.currentTarget.querySelectorAll('td').forEach(td => (td.style.background = '')))}
                        >
                          {columns.map((c) => (
                            <td key={c.key} style={{ ...tdStyle(c), padding: '13px 16px', color: 'var(--yool-black)', borderBottom: `1px solid ${GT.line}`, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', transition: 'background .1s' }}>
                              {c.render ? c.render(r) : r[c.key] ?? <span style={{ color: GT.muted }}>—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                  </React.Fragment>
                ) : null
              )}
          </tbody>
        </table>

        {isEmpty && (
          <div style={{ padding: "56px 24px", textAlign: "center" }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: GT.text }}>{(empty && empty.title) || "Sin resultados"}</p>
            <p style={{ margin: "4px auto 0", fontSize: 13, color: GT.muted, maxWidth: 340 }}>
              {(empty && empty.text) || "Ajusta la búsqueda o los filtros."}
            </p>
          </div>
        )}
      </div>

      {!isEmpty && (
        <div style={{ display: 'flex', alignItems: 'center', padding: '11px 18px', borderTop: `1px solid ${GT.border}`, background: 'var(--yool-page)', fontSize: 12, color: GT.muted }}>
          <span>
            {totalShown} {totalShown === 1 ? "resultado" : "resultados"}
            {totalAll != null && totalAll !== totalShown ? ` · de ${totalAll}` : ""}
          </span>
        </div>
      )}
    </div>
  );
}
